/**
 * Owns the entire submit pipeline for the pharma questionnaire page.
 *
 * Two paths converge into a single PUT:
 *
 * 1. No merged PDF — fire the PUT immediately with the current workflow
 *    document.
 * 2. Merged PDF — upload it to Firebase Storage first; once the upload
 *    completes (`progress === 100` and `downloadURL` is set on the
 *    redux slice) the PUT fires with `notes_path` pointing at the new
 *    object.
 *
 * The hook also guards against stale redux state: an `isAwaitingUploadRef`
 * sentinel ensures the post-upload effect only fires when the **current**
 * mount kicked off the upload, not because Redux still has a leftover
 * `downloadURL` from a previous page visit.
 *
 * @module pharmaQuestionaire/hooks/usePharmaQuestionnaireSubmission
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MN_JUDGE_ANSWER_SOURCE,
  MN_JUDGE_HUMAN_REVIEWED_ATTEMPT,
  postMnJudgeAssessment,
} from "../../../api/postCall/postMnJudgeAssessment";
import { PharmaBranchedQuestionnaire } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import { QuestionnaireReviewResponse } from "../../../data-model/questionnaireReview";
import { ToastMessages } from "../../../enums/toastMessages";
import {
  resetFileUploadState,
  uploadFileToFirebaseStorage,
} from "../../../redux/slice/fileUploadSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { buildMnJudgeAnswers } from "../utils/buildMnJudgeAnswers";
import { usePharmaQuestionnaireUpdate } from "./usePharmaQuestionnaireUpdate";

interface UsePharmaQuestionnaireSubmissionArgs {
  orderId: string | undefined;
  workflowDocument: PharmaBranchedQuestionnaire | null;
  questionnaireReview: QuestionnaireReviewResponse | null;
  mergedPdfToRender: File | null;
  uploadFilePath: string;
  userEmail: string;
}

interface UsePharmaQuestionnaireSubmissionReturn {
  /** Trigger the appropriate submit path (upload-then-PUT, or PUT). */
  handleSubmit: () => void;
  /**
   * Awaitable variant of {@link handleSubmit}: runs the same upload-then-PUT
   * pipeline but resolves with the outcome instead of toasting / navigating,
   * so callers can run it alongside other requests.
   */
  submitQuestionnaire: () => Promise<boolean>;
  /** Post-submit side effects (reset upload state, toast, navigate). */
  finishSubmission: (success: boolean) => void;
  /** `true` while the PUT is in flight. */
  saving: boolean;
  /** Current Firebase upload progress (0 when idle, 100 on completion). */
  progress: number;
}

export const usePharmaQuestionnaireSubmission = ({
  orderId,
  workflowDocument,
  questionnaireReview,
  mergedPdfToRender,
  uploadFilePath,
  userEmail,
}: UsePharmaQuestionnaireSubmissionArgs): UsePharmaQuestionnaireSubmissionReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    downloadURL,
    progress,
    error: uploadError,
  } = useSelector((state: RootState) => state.fileUploading);
  const { updateQuestionnaireReview, saving } = usePharmaQuestionnaireUpdate();

  /**
   * Tracks whether *this* mount initiated an upload. Without it, the
   * post-upload effect would fire on a fresh mount whenever Redux still
   * has a `downloadURL` from a previous page visit and re-PUT with stale
   * data, surfacing as a phantom "Failed to update" toast.
   */
  const isAwaitingUploadRef = useRef(false);

  /**
   * Resolver for the promise handed back by {@link submitQuestionnaire} while
   * the merged PDF uploads; settled by the post-upload effect below.
   */
  const pendingUploadResolverRef = useRef<((success: boolean) => void) | null>(
    null,
  );

  /**
   * Re-judges the form after a reviewer edit (MN judge pass 2). Advisory and
   * non-blocking: the verdict never gates the submit, so a judge outage is
   * logged and swallowed rather than surfaced.
   */
  const requestMnJudgeAssessment = useCallback(() => {
    const answers = buildMnJudgeAnswers(workflowDocument);
    if (!orderId || answers.length === 0) return;
    void postMnJudgeAssessment({
      identifier: orderId,
      answers,
      answer_source: MN_JUDGE_ANSWER_SOURCE.HUMAN_REVIEWED,
      attempt: MN_JUDGE_HUMAN_REVIEWED_ATTEMPT,
    }).catch((error: Error) =>
      logError(error, "MN judge re-assessment failed (non-blocking)"),
    );
  }, [orderId, workflowDocument]);

  const submitPut = useCallback(async (): Promise<boolean> => {
    const requestBody = {
      questionnaire: workflowDocument ?? ({} as PharmaBranchedQuestionnaire),
      notes_path: mergedPdfToRender
        ? uploadFilePath
        : (questionnaireReview?.details?.notes_path ?? ""),
      created_by: userEmail,
    };
    const result = await updateQuestionnaireReview(orderId ?? "", requestBody);
    if (!result?.success) return false;
    if (result.details?.qa_changed) requestMnJudgeAssessment();
    return true;
  }, [
    orderId,
    workflowDocument,
    mergedPdfToRender,
    uploadFilePath,
    questionnaireReview,
    userEmail,
    updateQuestionnaireReview,
    requestMnJudgeAssessment,
  ]);

  const finishSubmission = useCallback(
    (success: boolean) => {
      if (success) {
        dispatch(resetFileUploadState());
        navigate(`/pharma-pa-worklists/`);
        toast.success(ToastMessages.QUESTIONNAIRE_UPDATE_SUCCESS);
      } else {
        toast.error(ToastMessages.QUESTIONNAIRE_UPDATE_ERROR);
      }
    },
    [dispatch, navigate],
  );

  const submitQuestionnaire = useCallback((): Promise<boolean> => {
    if (!mergedPdfToRender) return submitPut();
    isAwaitingUploadRef.current = true;
    dispatch(resetFileUploadState());
    dispatch(uploadFileToFirebaseStorage(mergedPdfToRender, uploadFilePath));
    return new Promise<boolean>((resolve) => {
      pendingUploadResolverRef.current = resolve;
    });
  }, [dispatch, mergedPdfToRender, uploadFilePath, submitPut]);

  const handleSubmit = useCallback(() => {
    void submitQuestionnaire().then(finishSubmission);
  }, [submitQuestionnaire, finishSubmission]);

  /** Settles the promise handed back while the merged PDF was uploading. */
  const resolvePendingUpload = useCallback((success: boolean) => {
    const resolve = pendingUploadResolverRef.current;
    pendingUploadResolverRef.current = null;
    resolve?.(success);
  }, []);

  useEffect(() => {
    if (isAwaitingUploadRef.current && downloadURL && progress === 100) {
      isAwaitingUploadRef.current = false;
      void submitPut().then(resolvePendingUpload);
    }
  }, [downloadURL, progress, submitPut, resolvePendingUpload]);

  // A failed upload never reaches the effect above, so unblock any caller
  // waiting on `submitQuestionnaire` instead of leaving it pending forever.
  useEffect(() => {
    if (isAwaitingUploadRef.current && uploadError) {
      isAwaitingUploadRef.current = false;
      resolvePendingUpload(false);
    }
  }, [uploadError, resolvePendingUpload]);

  return {
    handleSubmit,
    submitQuestionnaire,
    finishSubmission,
    saving,
    progress,
  };
};
